import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"

import {
  CaretRightOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  LeftOutlined,
  PauseOutlined,
  RetweetOutlined,
  RightOutlined,
  SoundOutlined
} from "@ant-design/icons"
import classNames from "classnames"
import _, { debounce } from 'lodash'
import { useAppDispatch, useAppSelector } from "shared/config/store"
import { Dropdown, Slider, Tooltip } from "shared/ui"
import styled from "styled-components"

import {
  getPlayerCurrentTimeSelector,
  getPlayerDurationSelector,
  getPlayerIsFullscreenSelector,
  getPlayerIsLoopingSelector,
  getPlayerIsMutedSelector,
  getPlayerIsPlayingSelector,
  getPlayerPlaybackRateSelector,
  getPlayerVolumeSelector
} from "../model/selectors/filesPreviewSelectors"
import { filesPreviewActions, TabsNames } from "../model/slice/filesPreviewSlice"

import { ANNOTATION_COMMENT_ACTION, AssetAnnotations } from './AssetAnnotations'
import { StyledVideo } from "./styles"
import {
  calculateAnnotationCoordinates,
  CREATING_NEW_ANNOTATION_MODE,
  isSelectedAnnotationHasCoordinates
} from './utils/annotationUtils'

const generateCommentsForTimeline = (comments: any[]): { offset: number, text: string }[] => {
  const filteredComments = comments.filter(comment => comment.annotation?.offset)

  const commentsByOffset: Record<number, any[]> = {}

  filteredComments.forEach(comment => {
    const offset = comment.annotation.offset

    if (!commentsByOffset[offset]) {
      commentsByOffset[offset] = []
    }
    commentsByOffset[offset].push(comment)
  })

  return Object.entries(commentsByOffset).map(([offset, comments]) => {
    if (comments.length === 1) {
      return {
        offset: Number(offset),
        text: comments[0].author_name[0],
      }
    }

    return {
      offset: Number(offset),
      text: String(comments.length)
    }
  })
}

interface VideoViewerProps {
  src: string
  annotations: any[]
  comments: any[]
  isCommentsLoading: boolean
  isAssetPlaying: boolean
  selectedAnnotation: any,
  newAnnotation: any,
  userName: string,
  isShowAnnotationLayer: boolean,
  isShowAnnotationMarkers: boolean,
  isCreatingNewAnnotation: boolean,
  creatingNewAnnotationMode: CREATING_NEW_ANNOTATION_MODE,
  onCanPlay: () => void
  onError: () => void
  onAnnotationSubmit: (annotation: any) => void
  onAnnotationDelete: (annotation: any) => void
  onAnnotationEdit: (annotation: any) => void
  onAnnotationResolve: (annotation: any) => void
  onVolumeHover?: (isHovered: boolean) => void
}

const STAGE_MARGIN = 0 // Margin for the stage

export const VideoViewer = ({
  onCanPlay,
  onError,
  src,
  comments,
  annotations,
  isCommentsLoading,
  isAssetPlaying,
  userName,
  selectedAnnotation,
  newAnnotation,
  isShowAnnotationLayer,
  isShowAnnotationMarkers,
  isCreatingNewAnnotation,
  creatingNewAnnotationMode,
  onAnnotationSubmit,
  onAnnotationDelete,
  onAnnotationEdit,
  onAnnotationResolve,
  onVolumeHover,
}: VideoViewerProps) => {
  const dispatch = useAppDispatch()

  const reduxCurrentTime = useAppSelector(getPlayerCurrentTimeSelector)
  const reduxDuration = useAppSelector(getPlayerDurationSelector)
  const isPlaying = useAppSelector(getPlayerIsPlayingSelector)
  const volume = useAppSelector(getPlayerVolumeSelector)
  const isMuted = useAppSelector(getPlayerIsMutedSelector)
  const isFullscreen = useAppSelector(getPlayerIsFullscreenSelector)
  const isLooping = useAppSelector(getPlayerIsLoopingSelector)
  const playbackRate = useAppSelector(getPlayerPlaybackRateSelector)

  const videoRef = useRef(null)
  const videoContainerRef = useRef(null)
  const progressBarRef = useRef(null)
  const volumeTimeoutRef = useRef(null)
  const isDraggingRef = useRef(false)
  const videoContentRef = useRef(null)
  const lastReduxTimeRef = useRef(0) // Добавляем ref для отслеживания последнего времени из Redux

  const [isLoaded, setIsLoaded] = useState(false)
  const [showVolumeSlider, setShowVolumeSlider] = useState(false)
  const [sliderValue, setSliderValue] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [naturalWidth, setNaturalWidth] = useState(0)
  const [naturalHeight, setNaturalHeight] = useState(0)

  const commentsForTimeline = generateCommentsForTimeline(comments)

  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [containerOffset, setContainerOffset] = useState({ x: 0, y: 0 })  // Offset for image container
  const [assetSize, setAssetSize] = useState({ width: 0, height: 0 })

  const isCommentVisible = !isAssetPlaying && (newAnnotation || isSelectedAnnotationHasCoordinates(selectedAnnotation))

  // Вычисляет размеры видео с учетом пропорций и контейнера
  const calculateAssetContentSize = () => {
    // Проверяем наличие необходимых refs
    if (!videoRef.current || !videoContainerRef.current || !videoContentRef.current) {
      return { width: 0, height: 0 }
    }

    // Получаем естественные размеры видео
    const videoNaturalWidth = naturalWidth
    const videoNaturalHeight = naturalHeight

    // Вычисляем соотношение сторон (width/height)
    const aspectRatio = videoNaturalHeight > 0 ? videoNaturalWidth / videoNaturalHeight : 0

    // Вычисляем максимальную высоту видео с учетом контейнера
    const maxVideoHeight = Math.min(naturalHeight, videoContentRef.current.clientHeight - 24)

    // Вычисляем смещение контейнера для центрирования
    const containerOffset = {
      x: -STAGE_MARGIN / 2,
      y: -STAGE_MARGIN / 2
    }

    let assetWidth, assetHeight

    if (aspectRatio >= 1) {
      // Горизонтальное видео (ширина >= высоты)
      assetHeight = maxVideoHeight
      assetWidth = assetHeight * aspectRatio
    } else {
      // Вертикальное видео (высота > ширины)
      assetHeight = maxVideoHeight
      assetWidth = assetHeight * aspectRatio
    }

    return { width: assetWidth, height: assetHeight, containerOffset }
  }

  const assetContentSize = calculateAssetContentSize()

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const handleLoadedMetadata = () => {
      // native video content width and height
      const width = video.videoWidth
      const height = video.videoHeight
      setNaturalWidth(width)
      setNaturalHeight(height)
    }

    video.addEventListener('loadedmetadata', handleLoadedMetadata)

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata)
    }
  }, [])

  useEffect(() => {
    if(!selectedAnnotation) dispatch(filesPreviewActions.setReplyingCommentId(null))
  }, [selectedAnnotation])

  const updateProgressBar = useCallback((percent) => {
    if (progressBarRef.current) {
      const boundedPercent = Math.max(0, Math.min(100, percent))
      progressBarRef.current.style.width = `${boundedPercent}%`
    }
  }, [])

  const resetAnnotations = () => {
    dispatch(filesPreviewActions.setSelectedAnnotation(null))
    dispatch(filesPreviewActions.setNewAnnotation(null))
    dispatch(filesPreviewActions.setIsCreatingNewAnnotation(false))
  }

  useEffect(() => {
    if(isPlaying) {
      resetAnnotations()
    }
  }, [isPlaying])


  useEffect(() => {
    const handleResize = (videoRef) => {
      if (!videoRef.current) return
      const { width, height, containerOffset } = calculateAssetContentSize()
      setAssetSize({ width, height })
      setContainerOffset(containerOffset)
    }

    const debouncedHandleResize = debounce(handleResize.bind(null, videoRef, videoContainerRef), 200)
    window.addEventListener('resize', debouncedHandleResize)

    return () => {
      window.removeEventListener('resize', debouncedHandleResize)
      debouncedHandleResize.cancel() // Cancel any pending debounced calls
    }
  }, [videoRef.current, naturalWidth, naturalHeight])

  useEffect(() => {
    if (!videoRef.current) return
    const { width, height, containerOffset } = calculateAssetContentSize()
    setAssetSize({ width, height })
    setContainerOffset(containerOffset)

  }, [isCreatingNewAnnotation, isShowAnnotationLayer, isShowAnnotationMarkers, videoRef.current, naturalWidth, naturalHeight])

  useLayoutEffect(() => {
    if (videoRef.current) {
      videoRef.current.pause()
      videoRef.current.src = src
      videoRef.current.currentTime = 0
    }

    const resetPlayer = () => {
      dispatch(filesPreviewActions.setPlayerCurrentTime(0))
      dispatch(filesPreviewActions.setPlayerDuration(0))
      dispatch(filesPreviewActions.setPlayerIsPlaying(false))
      dispatch(filesPreviewActions.setPlayerIsMuted(false))
      dispatch(filesPreviewActions.setPlayerIsLooping(false))
      dispatch(filesPreviewActions.setPlayerPlaybackRate(1))
    }

    setCurrentTime(0)
    setSliderValue(0)
    updateProgressBar(0)

    resetPlayer()

    return () => {
      if (videoRef.current) {
        videoRef.current.src = ''
      }

      resetPlayer()
    }
  }, [src])

  useEffect(() => {
    const video = videoRef.current

    if (!video) return

    const handleTimeUpdate = () => {
      const currentVideoTime = video.currentTime
      setCurrentTime(currentVideoTime)
      handleReduxTimeUpdate(currentVideoTime)

      // Обновляем прогресс бар и слайдер только если пользователь не перетаскивает
      if (!isDraggingRef.current && video.duration) {
        const percent = (currentVideoTime / video.duration) * 100
        updateProgressBar(percent)
        setSliderValue(percent)
      }
    }

    const handleLoadedMetadata = () => {
      dispatch(filesPreviewActions.setPlayerDuration(video.duration))
    }

    const handlePlay = () => {
      dispatch(filesPreviewActions.setPlayerIsPlaying(true))
    }

    const handlePause = () => {
      dispatch(filesPreviewActions.setPlayerIsPlaying(false))
    }

    video.addEventListener('timeupdate', handleTimeUpdate)
    video.addEventListener('loadedmetadata', handleLoadedMetadata)
    video.addEventListener('play', handlePlay)
    video.addEventListener('pause', handlePause)

    const handleFullscreenChange = () => {
      dispatch(filesPreviewActions.setPlayerIsFullscreen(!!document.fullscreenElement))
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate)
      video.removeEventListener('loadedmetadata', handleLoadedMetadata)
      video.removeEventListener('play', handlePlay)
      video.removeEventListener('pause', handlePause)
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  }, [dispatch, isPlaying])

  useEffect(() => {
    if (videoRef.current && Math.abs(reduxCurrentTime - lastReduxTimeRef.current) > 0.1) {
      lastReduxTimeRef.current = reduxCurrentTime
      videoRef.current.currentTime = reduxCurrentTime

      const percent = (videoRef.current.currentTime / (videoRef.current.duration || 1)) * 100
      updateProgressBar(percent)
      setSliderValue(percent)
    }
  }, [reduxCurrentTime])

  useEffect(() => {
    const video = videoRef.current

    if (video) {
      video.loop = isLooping
      video.playbackRate = playbackRate

      if (isMuted) {
        video.volume = 0
      } else {
        video.volume = volume
      }
    }
  }, [isLooping, playbackRate, isMuted, volume])

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code === 'Space' && !e.target.matches('input, textarea, [contenteditable]')) {
        e.preventDefault()
        handlePlayPause()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isPlaying])

  useEffect(() => {
    if (isPlaying) {
      videoRef.current.play()
    } else {
      videoRef.current.pause()
    }
  }, [isPlaying])

  const handleOnCanPlay = () => {
    onCanPlay()
    setIsLoaded(true)
  }

  const handlePlayPause = () => {
    const video = videoRef.current
    if (!video) return

    if (isPlaying) {
      video.pause()
    } else {
      video.play()
    }
  }

  const handleReduxTimeUpdate = useCallback(_.debounce((newTime) => {
    dispatch(filesPreviewActions.setPlayerCurrentTime(newTime))
  }, 400), [dispatch])

  const handleSliderChange = useCallback(_.throttle((value) => {
    isDraggingRef.current = true

    setSliderValue(value)
    updateProgressBar(value)

    const video = videoRef.current
    if (video) {
      const newTime = (value / 100) * video.duration
      video.currentTime = newTime
    }
  }, 10), [updateProgressBar])

  const handleSliderAfterChange = useCallback(() => {
    const video = videoRef.current

    if (video) {
      const newTime = video.currentTime
      handleReduxTimeUpdate(newTime)
    }

    setTimeout(() => {
      isDraggingRef.current = false
    }, 40)
  }, [handleReduxTimeUpdate])

  const handleVolumeChange = (value) => {
    const newVolume = value / 100
    dispatch(filesPreviewActions.setPlayerVolume(newVolume))

    if (newVolume === 0) {
      dispatch(filesPreviewActions.setPlayerIsMuted(true))
    } else if (isMuted) {
      dispatch(filesPreviewActions.setPlayerIsMuted(false))
    }
  }

  const toggleMute = () => {
    dispatch(filesPreviewActions.setPlayerIsMuted(!isMuted))
  }

  const handleCommentClick = useCallback((timestamp) => {

    if (videoRef.current) {
      videoRef.current.pause()
      videoRef.current.currentTime = timestamp

      dispatch(filesPreviewActions.setActiveTab(TabsNames.COMMENTS))
      dispatch(filesPreviewActions.setPlayerCurrentTime(timestamp))

      setTimeout(() => {
        const commentElement = document.querySelector(`div[data-timestamp="${timestamp}"]`)
        if (commentElement) {
          commentElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 100)

      resetAnnotations()
    }
  }, [dispatch])

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      videoContainerRef.current.requestFullscreen().catch(err => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`)
      })
    } else {
      document.exitFullscreen()
    }
  }

  const toggleLoop = () => {
    dispatch(filesPreviewActions.setPlayerIsLooping(!isLooping))
  }

  const changePlaybackRate = ({ key }) => {
    dispatch(filesPreviewActions.setPlayerPlaybackRate(parseFloat(key)))
  }

  const handleVolumeMouseEnter = () => {
    clearTimeout(volumeTimeoutRef.current)
    setShowVolumeSlider(true)
    onVolumeHover?.(true)
  }

  const handleVolumeMouseLeave = () => {
    volumeTimeoutRef.current = setTimeout(() => {
      setShowVolumeSlider(false)
      onVolumeHover?.(false)
    }, 800)
  }

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  }

  const playbackRateItems = [
    { key: '0.25', label: '0.25x' },
    { key: '0.5', label: '0.5x' },
    { key: '0.75', label: '0.75x' },
    { key: '1', label: '1x' },
    { key: '1.25', label: '1.25x' },
    { key: '1.5', label: '1.5x' },
    { key: '2', label: '2x' }
  ]

  const handleSkipBackward = () => {
    if (videoRef.current) {
      const newTime = Math.max(0, videoRef.current.currentTime - 1)
      videoRef.current.currentTime = newTime
      dispatch(filesPreviewActions.setPlayerCurrentTime(newTime))
    }
  }

  const handleSkipForward = () => {
    if (videoRef.current) {
      const newTime = Math.min(videoRef.current.duration, videoRef.current.currentTime + 1)
      videoRef.current.currentTime = newTime
      dispatch(filesPreviewActions.setPlayerCurrentTime(newTime))
    }
  }

  // Function to pause the video player
  const pauseVideo = () => {
    if (videoRef.current && isPlaying) {
      videoRef.current.pause()
    }
  }

  // Function to scroll to the annotation by its ID
  const scrollToAnnotation = (annotation: any) => {
    const annotationData = annotation.data
    const commentElement = document.getElementById(`${annotationData.id}`)
    if (commentElement) {
      commentElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  const handleAnnotationSelect = (annotation: any) => {
    if (!annotation) {
      dispatch(filesPreviewActions.setSelectedAnnotation(null))
      return
    }
    // Pause the video when selecting an annotation
    pauseVideo()

    dispatch(filesPreviewActions.setReplyingCommentId(null))

    const containerWidth = videoContainerRef.current.clientWidth
    const containerHeight = videoContainerRef.current.clientHeight
    const normalizedAnnotation = {
      ...annotation,
      geometry: {
        absolute: {
          x: annotation.x / 100 * containerWidth,
          y: annotation.y / 100 * containerHeight,
          width: annotation.width / 100 * containerWidth,
          height: annotation.height / 100 * containerHeight
        },
        relative: {
          x: annotation.x,
          y: annotation.y,
          width: annotation.width,
          height: annotation.height
        }
      }
    }

    scrollToAnnotation(normalizedAnnotation)
    dispatch(filesPreviewActions.setSelectedAnnotation(normalizedAnnotation))
    dispatch(filesPreviewActions.setNewAnnotation(null))
  }


  const handleAnnotationSubmit = (annotation: any) => {
    const { width: imgWidth, height: imgHeight } = calculateAssetContentSize()
    const { coordinates, xPosPercent, yPosPercent } = calculateAnnotationCoordinates(
      annotation,
      imgWidth,
      imgHeight,
      STAGE_MARGIN
    )

    onAnnotationSubmit({ ...annotation, x: xPosPercent, y: yPosPercent, coordinates, offset: currentTime })
    dispatch(filesPreviewActions.setSelectedAnnotation(null))
    dispatch(filesPreviewActions.setNewAnnotation(null))
  }

  const handleAnnotationDelete = (annotation: any) => {
    onAnnotationDelete(annotation)
  }

  const handleAnnotationEdit = (annotation: any) => {
    onAnnotationEdit(annotation)
    dispatch(filesPreviewActions.setSelectedAnnotation(null))
  }

  const handleAnnotationCommentClose = () => {
    dispatch(filesPreviewActions.setSelectedAnnotation(null))
    dispatch(filesPreviewActions.setNewAnnotation(null))
    dispatch(filesPreviewActions.setIsCreatingNewAnnotation(false))
  }

  const handleCreateNewAnnotation = (annotation: any) => {
    dispatch(filesPreviewActions.setIsCreatingNewAnnotation(true))
    dispatch(filesPreviewActions.setSelectedAnnotation(null))
    dispatch(filesPreviewActions.setNewAnnotation(annotation))
  }

  const handleClearNewAnnotation = () => {
    dispatch(filesPreviewActions.setSelectedAnnotation(null))
    dispatch(filesPreviewActions.setNewAnnotation(null))
  }

  const handleStartCreatingNewAnnotation = () => {
    pauseVideo()
  }

  const handleAnnotationCommentAction = (action: ANNOTATION_COMMENT_ACTION, annotation: any ) => {
    switch (action) {
    case ANNOTATION_COMMENT_ACTION.ADD:
      handleAnnotationSubmit(annotation)
      dispatch(filesPreviewActions.setNewAnnotation(null))
      return
    case ANNOTATION_COMMENT_ACTION.EDIT:
      handleAnnotationEdit(annotation)
      dispatch(filesPreviewActions.setSelectedAnnotation(null))
      return
    case ANNOTATION_COMMENT_ACTION.DELETE:
      handleAnnotationDelete(annotation)
      dispatch(filesPreviewActions.setSelectedAnnotation(null))
      return
    case ANNOTATION_COMMENT_ACTION.OPEN_REPLY:
      dispatch(filesPreviewActions.setSelectedAnnotation(annotation))
      return
    case ANNOTATION_COMMENT_ACTION.RESOLVE:
      onAnnotationResolve(annotation)
      dispatch(filesPreviewActions.setSelectedAnnotation(null))
      return
    }
  }

  const handleReplyToComment = (annotation: any) => {
    dispatch(filesPreviewActions.setReplyingCommentId(annotation.data.id))
  }

  const handleCanvasClickOutside = () => {
    if (selectedAnnotation) {
      dispatch(filesPreviewActions.setSelectedAnnotation(null))
    }
  }

  const boundingRect = videoContainerRef.current?.getBoundingClientRect()

  const getVideoElementStyle = () => {
    const { width: videoWidth, height: videoHeight } = calculateAssetContentSize()
    return {
      width: '100%',
      height: '100%',
      maxWidth: `${videoWidth}px`,
      maxHeight: `${videoHeight}px`
    }
  }

  return (
    <SVideoContainer
      ref={videoContainerRef}
    >
      <SVideoContent ref={videoContentRef}>

        <StyledVideo
          style={getVideoElementStyle()}
          onCanPlay={handleOnCanPlay}
          ref={videoRef}
          preload="auto"
          onError={onError}
          className={classNames({ 'not-loaded': !isLoaded })}
          onClick={handlePlayPause}
        />

        <AssetAnnotations
          stageMargin={STAGE_MARGIN}
          assetContentSize={assetContentSize}
          className="asset-annotations"
          parentRef={videoRef}
          assetRef={videoRef}
          assetBoundingBox={assetSize}
          isCanvasVisible={isShowAnnotationLayer}
          isCommentVisible={isCommentVisible}
          position={position}
          isDragging={isDragging}
          annotations={annotations}
          newAnnotation={newAnnotation}
          selectedAnnotation={selectedAnnotation}
          creatingNewAnnotationMode={creatingNewAnnotationMode}
          onAnnotationSelect={handleAnnotationSelect}
          onAnnotationSubmit={handleAnnotationSubmit}
          onCreateNewAnnotation={handleCreateNewAnnotation}
          onClearNewAnnotation={handleClearNewAnnotation}
          onStartCreatingNewAnnotation={handleStartCreatingNewAnnotation}
          showMarkers={isShowAnnotationMarkers}
          onCommentClose={handleAnnotationCommentClose}
          onCommentAction={handleAnnotationCommentAction}
          onReplyToComment={handleReplyToComment}
          boundingRect={boundingRect}
          containerOffset={containerOffset}
          onCanvasClickOutside={handleCanvasClickOutside}
        />
      </SVideoContent>

      <SVideoControlsContainer>
        <SVideoProgressContainer>
          <SVideoProgress>
            <SProgressBackground />

            <SProgressBar
              ref={progressBarRef}
            />

            <SSliderContainer>
              <Slider
                onChange={handleSliderChange}
                onChangeComplete={handleSliderAfterChange}
                value={sliderValue}
                tooltip={{ open: false }}
                min={0}
                max={100}
                step={0.1}
              />
            </SSliderContainer>

            {commentsForTimeline && commentsForTimeline.map(comment => (
              <SCommentMarker
                key={comment.offset}
                style={{ left: `${(comment.offset / reduxDuration) * 100}%` }}
                onClick={() => handleCommentClick(comment.offset)}
                className={currentTime >= comment.offset - 0.5 && currentTime <= comment.offset + 0.5 ? 'active' : ''}
              >
                <SCommentAvatar>
                  {comment.text}
                </SCommentAvatar>
              </SCommentMarker>
            ))}
          </SVideoProgress>
        </SVideoProgressContainer>

        <SVideoControlsBottom>
          <SVideoControlsLeft>
            <SControlButton onClick={handlePlayPause}>
              {isPlaying ? <PauseOutlined /> : <CaretRightOutlined />}
            </SControlButton>

            <SControlButton
              onClick={toggleLoop}
              className={isLooping ? 'active' : ''}
              title="Repeat"
            >
              <RetweetOutlined />
            </SControlButton>

            <Dropdown
              menu={{
                items: playbackRateItems,
                onClick: changePlaybackRate,
                selectable: true,
                defaultSelectedKeys: ['1']
              }}
              placement="top"
              trigger={['click']}
            >
              <SControlButton title="Speed">
                <SPlaybackRate>
                  {playbackRate}
x
                </SPlaybackRate>
              </SControlButton>
            </Dropdown>
          </SVideoControlsLeft>

          <SVideoControlsCenter>
            <SProgressIndicator>
              <Tooltip title="Move 1 second back">
                <SSkipButton onClick={handleSkipBackward}>
                  <LeftOutlined />
                </SSkipButton>
              </Tooltip>

              {formatTime(currentTime)}

              {' '}

/
              {' '}

              {formatTime(reduxDuration)}

              <Tooltip title="Move 1 second forward">
                <SSkipButton onClick={handleSkipForward}>
                  <RightOutlined />
                </SSkipButton>
              </Tooltip>
            </SProgressIndicator>
          </SVideoControlsCenter>

          <SVideoControlsRight>
            <SVolume
              onMouseEnter={handleVolumeMouseEnter}
              onMouseLeave={handleVolumeMouseLeave}
            >
              <SControlButton onClick={toggleMute}
                title={isMuted ? 'Enable sound' : 'Disable sound'}
              >
                {isMuted ? <SMutedIcon /> : <SoundOutlined />}
              </SControlButton>

              {showVolumeSlider && (
                <SVolumeSlider
                  onMouseEnter={handleVolumeMouseEnter}
                  onMouseLeave={handleVolumeMouseLeave}
                >
                  <Slider
                    vertical
                    min={0}
                    max={100}
                    value={isMuted ? 0 : volume * 100}
                    onChange={handleVolumeChange}
                    tooltip={{ open: false }}
                  />
                </SVolumeSlider>
              )}
            </SVolume>

            <SControlButton onClick={toggleFullscreen}
              title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            >
              {isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
            </SControlButton>
          </SVideoControlsRight>
        </SVideoControlsBottom>
      </SVideoControlsContainer>
    </SVideoContainer>
  )
}

const SVideoContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background-color: var(--color-black);
`

const SVideoControlsContainer = styled.div`
  padding: 12px 16px 16px;
  background-color: rgba(0, 0, 0, 0.75);
  transition: opacity 0.3s;
  z-index: 10;
`

const SVideoProgressContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 8px;
`

const SVideoProgress = styled.div`
  position: relative;
  width: 100%;
  height: 4px;
  margin: 10px 0;
`

const SProgressBackground = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
`

const SProgressBar = styled.div`
  position: absolute;
  height: 100%;
  background-color: var(--color-primary);
  border-radius: 2px;
  transition: none;
`

const SSliderContainer = styled.div`
  position: absolute;
  width: 100%;
  top: -8px;
  height: 20px;
  cursor: pointer;
  
  .ant-slider {
    margin: 0;
    padding: 0;
    height: 20px;
    
    .ant-slider-rail {
      display: none;
    }
    
    .ant-slider-track {
      background-color: transparent;
    }
    
    .ant-slider-handle {
      opacity: 0;
      border-color: var(--color-primary);
      background-color: var(--color-primary);
      transition: opacity 0.2s;
      margin-top: 3px;
      width: 10px;
      height: 10px;

      &::after {
        background-color: var(--color-primary);
      }
    }
    
    &:hover {
      .ant-slider-handle {
        opacity: 1;
      }
    }
  }
`

const SVideoControlsBottom = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const SVideoControlsLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const SVideoControlsCenter = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  justify-content: center;
`

const SProgressIndicator = styled.div`
  color: var(--color-white);
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  user-select: none;
`

const SVideoControlsRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const SControlButton = styled.button`
  background: transparent;
  border: none;
  color: var(--color-white);
  font-size: 20px;
  cursor: pointer;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s;
  
  &:hover, &.active {
    color: var(--color-primary);
  }
`

const SPlaybackRate = styled.span`
  font-size: 14px;
  font-weight: 500;
`

const SVolume = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`

const SVolumeSlider = styled.div`
  position: absolute;
  bottom: 40px;
  left: 0;
  height: 100px;
  width: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(32, 32, 32, 0.9);
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
  
  &:before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -20px;
    height: 20px;
    background: transparent;
  }
  
  .ant-slider {
    height: 80px;
    margin: 0 10px;
    
    .ant-slider-rail {
      background-color: rgba(255, 255, 255, 0.2);
    }
    
    .ant-slider-track {
      background-color: var(--color-primary);
    }
    
    .ant-slider-handle {
      border-color: var(--color-primary);
      background-color: #fff;

      &::after {
        background-color: var(--color-primary);
      }
    }
  }
`

const SCommentMarker = styled.div`
  position: absolute;
  top: -28px;
  width: 22px;
  height: 22px;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  cursor: pointer;
  z-index: 5;
  transition: transform 0.2s, color 0.2s;
  color: var(--color-primary);
  
  &:hover, &.active {
    color: var(--color-primary);
  }

  &::before {
    content: '';
    position: absolute;
    width: 4px;
    height: 4px;
    bottom: -10px;
    border-radius: 50%;
    background-color: white;
  }
`

const SMutedIcon = styled(SoundOutlined)`
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    width: 2px;
    height: 18px;
    background-color: #fff;
    transform: rotate(45deg);
    top: 3px;
    left: 11px;
  }
`

const SCommentAvatar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #575757;
  color: white;
  font-size: 10px;
  font-weight: bold;
  transition: transform 0.2s, border 0.2s;

  ${SCommentMarker}:hover & {
    border: 1px solid white;
    transform: scale(1.2);
  }

  ${SCommentMarker}.active & {
    transform: scale(1.2);
  }
`

const SSkipButton = styled.button`
  background: transparent;
  border: none;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s;
  
  &:hover {
    color: var(--color-primary);
  }
`

const SVideoContent = styled.div`
  position: relative;
  display: flex;
  height: calc(100% - 98px);
  align-items: center;
  justify-content: center;
  padding: 0;
`
